// Booking detail — fetches /admin/bookings/:reference and renders
// timeline + customer + vendor + financial blocks.

import { useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { resolveBillableDays } from '@drivecars/shared';
import { DetailLayout, DetailSection, DefRow, DetailTabs, type DetailTab } from '@drivecars/admin-ui';
import { EntityActivity } from '@/components/EntityActivity';
import { StatusPill } from '@drivecars/admin-ui';
import { Avatar } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { ApiError } from '@/lib/api';
import { AssignmentBadge } from './AssignmentBadge';
import { bidWorkFor, bidIsUsable, canConfirmWithoutMatch, matchInputForBid, type BidWork } from './booking-bid-state';
import {
  cancelBooking,
  completeBooking,
  confirmBooking,
  fetchAdminBookingDetail,
  fetchBookingBids,
  listHosts,
  markHostPayoutPaid,
  matchBooking,
  requestBookingPayment,
  type AdminBookingDetail,
  type BookingMoney,
  type MatchBookingResult,
  type PaymentRequestReason,
  type PaymentRequestResult,
} from '@/lib/admin-api';

const fmtMoney = (v: number, currency = 'EUR') =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(v);

const fmtDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : '—';

const fmtDay = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** Is this booking an airport transfer rather than a car rental? */
const isTransferBooking = (b: { product?: string | null }) =>
  b.product === 'airport-transfers';

/**
 * What KIND of booking this is, for the page's eyebrow.
 *
 * Two independent axes, deliberately both surfaced: `product` says what was
 * sold (a ride vs a car), `bookingType` says who fulfils it (our crew vs the
 * Avis supplier). An operator acting on "Avis rental" has different options
 * from one acting on a marketplace rental, and the page used to say only
 * "Booking".
 */
function bookingTypeLabel(b: { product?: string | null; bookingType?: string | null }): string {
  if (isTransferBooking(b)) return 'Airport transfer';
  return b.bookingType === 'avis' ? 'Avis rental' : 'Car rental';
}

/** Local pickup/return hour (0-23) → " HH:00", or '' when unset. */
const fmtHour = (h: number | null | undefined) =>
  h == null || !Number.isFinite(h) || h < 0 || h > 23 ? '' : ` ${String(Math.floor(h)).padStart(2, '0')}:00`;

/** Locked-amount-minor → human-readable string. The locked minor is the Stripe
 *  charge expressed in the minor unit of `paymentLockedCurrency` (e.g. cents).
 *  Zero-decimal currencies (JPY, KRW, …) are uncommon for our markets — keep
 *  the dumb /100 conversion; revisit if we add JPY pricing. */
function fmtLockedAmount(minor: number | null, currency: string | null): string {
  if (minor == null || !currency) return '—';
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

const METHOD_COLORS: Record<'card' | 'klarna' | 'crypto', { bg: string; ink: string }> = {
  card: { bg: '#dbeafe', ink: '#1e40af' },
  klarna: { bg: '#fce7f3', ink: '#9d174d' },
  crypto: { bg: '#fef3c7', ink: '#92400e' },
};

function MethodChip({ method }: { method: 'card' | 'klarna' | 'crypto' }) {
  const c = METHOD_COLORS[method];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize"
      style={{ backgroundColor: c.bg, color: c.ink }}
    >
      {method}
    </span>
  );
}

export function BookingDetail() {
  // See CarDetail for rationale on `strict: false`.
  const params = useParams({ strict: false }) as { reference?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const reference = params.reference!;
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'bookings', reference],
    queryFn: ({ signal }) => fetchAdminBookingDetail(reference, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'bookings'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  const confirmMut = useMutation({ mutationFn: confirmBooking, onSuccess: invalidate });
  const completeMut = useMutation({ mutationFn: completeBooking, onSuccess: invalidate });
  const cancelMut = useMutation({
    mutationFn: ({ ref, reason }: { ref: string; reason?: string }) => cancelBooking(ref, reason),
    onSuccess: invalidate,
  });
  // Klarna/crypto host-payout finalisation. Card bookings auto-settle via
  // Stripe Connect; the backend 409s if called for `card` or already-paid rows
  // so we just surface the error message.
  const markHostPayoutMut = useMutation({
    mutationFn: markHostPayoutPaid,
    onSuccess: invalidate,
  });

  if (isLoading) {
    return (
      <DetailLayout
        title="Loading…"
        backTo="/bookings"
        backLabel="All bookings"
      >
        <div className="rounded-xl border border-[color:var(--bo-line)] bg-white p-5 text-[12.5px] text-[color:var(--bo-mute)]">
          Loading booking detail…
        </div>
      </DetailLayout>
    );
  }
  if (error || !data) {
    return (
      <DetailLayout title="Not found" backTo="/bookings" backLabel="All bookings">
        <div className="rounded-xl border border-[#fecaca] bg-[#fff1f2] p-5 text-[12.5px] text-[#9f1239]">
          {error ? (error as Error).message : 'No booking with that reference.'}
        </div>
      </DetailLayout>
    );
  }

  const b = data;
  const busy = confirmMut.isPending || completeMut.isPending || cancelMut.isPending;

  // Booked (purchase) currency: the ledger figures above are in the primary
  // currency; convert to what the customer transacted in via the snapshotted
  // rate (rateToPrimary: 1 booked unit x rate = 1 primary, so primary/rate).
  const bookedCur = b.purchaseCurrency ?? null;
  const bookedDiffers = !!bookedCur && bookedCur !== b.currency;
  const toBooked = (v: number) =>
    b.purchaseFxRate && b.purchaseFxRate > 0 ? v / b.purchaseFxRate : v;
  const fmtBooked = (v: number) => fmtMoney(toBooked(v), bookedCur ?? b.currency);

  // Vendor base currency (car pricing currency, e.g. MAD). Settlement figures
  // (cash to crew) show in the vendor's own currency: EUR ledger → vendor =
  // eur / rateToPrimary, looked up from displayCurrencies. Falls back to the
  // EUR figure when the currency/rate is unknown.
  const vendorCur = b.vendorCurrency ?? null;
  const vendorRate =
    (vendorCur && b.displayCurrencies?.find((d) => d.code === vendorCur)?.rateToPrimary) || null;
  const vendorConvertible = !!vendorCur && !!vendorRate && vendorRate > 0;
  const toVendor = (v: number) => (vendorConvertible ? v / (vendorRate as number) : v);
  const fmtVendor = (v: number) => fmtMoney(toVendor(v), vendorCur ?? b.currency);

  const customerSection = (
    <DetailSection title="Customer">
      <div className="mb-3 flex items-center gap-3">
        <Avatar
          name={
            [b.customer.firstName, b.customer.lastName].filter(Boolean).join(' ') ||
            b.customer.email ||
            'guest'
          }
          size={36}
        />
        {/* min-w-0 + break-all: generated rider addresses
            (rider-447821015382@phone.drivecars.local) are long single tokens
            that otherwise overflow the card and collide with its border. */}
        <div className="min-w-0">
          <div className="break-all text-[14px] font-semibold">
            {[b.customer.firstName, b.customer.lastName].filter(Boolean).join(' ') ||
              b.customer.email}
          </div>
          <div className="break-all text-[12px] text-[color:var(--bo-mute)]">
            {b.customer.email}
          </div>
        </div>
      </div>
      <DefRow label="Phone">{b.customer.phone ?? '—'}</DefRow>
      {b.customer.notes ? <DefRow label="Notes">{b.customer.notes}</DefRow> : null}
      <button
        type="button"
        onClick={() =>
          void navigate({
            to: '/customers/$id' as never,
            params: { id: String(b.customer.id) } as never,
          })
        }
        className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
      >
        View customer →
      </button>
    </DetailSection>
  );

  // Omitted entirely when the booking carries no fulfilment data. A card whose
  // only content is "Mode: —" is not information; it takes a full column to say
  // nothing, which is what made the old layout feel padded. The dates/location
  // an operator needs are already on Booking details and Car & vendor.
  const fulfilmentSection = !b.fulfilment?.pickupType ? null : (
    <DetailSection title="Fulfilment">
      <DefRow label="Mode">
        {b.fulfilment.pickupType === 'delivery' ? 'Delivery to customer' : 'Pickup'}
      </DefRow>
      {/* Always show the dropoff/pickup location for delivery/pickup. Prefer
          the customer's chosen location (airport/station) snapshotted on the
          booking; fall back to the car's city. */}
      <DefRow
        label={b.fulfilment.pickupType === 'delivery' ? 'Delivery location' : 'Pickup location'}
      >
        {b.fulfilment.pickupLocationName ??
          (b.location ? (b.location.name ?? b.location.slug) : '—')}
      </DefRow>
      {b.fulfilment.deliveryAddress ? (
        <DefRow label="Delivery address">{b.fulfilment.deliveryAddress}</DefRow>
      ) : null}
    </DetailSection>
  );

  const bookingSection = (
    <DetailSection title="Booking details">
      <DefRow label="Dates">
        {fmtDay(b.dateFrom)}{fmtHour(b.pickupHour)} → {fmtDay(b.dateTo)}{fmtHour(b.returnHour)}
      </DefRow>
      <DefRow label="Days booked">
        {resolveBillableDays({
          pickupAt: b.pickupAt ?? null,
          returnAt: b.returnAt ?? null,
          dateFrom: b.dateFrom ?? '',
          dateTo: b.dateTo ?? '',
        })}
      </DefRow>
      <DefRow label="Vehicles">{b.units}</DefRow>
    </DetailSection>
  );

  // A TRANSFER HAS NO CAR. The class is chosen at the kerb, so "Car —" and
  // "Slug —" were two dead rows on every transfer. It has a journey instead,
  // and that is what an operator needs when someone rings about it.
  const carVendorSection = (
    <DetailSection title={isTransferBooking(b) ? 'Journey & crew' : 'Car & vendor'}>
      {isTransferBooking(b) ? (
        <>
          <DefRow label="Pick-up">
            {b.fulfilment?.pickupLocationName ?? b.location?.name ?? '—'}
          </DefRow>
          <DefRow label="Destination">{b.destination ?? '—'}</DefRow>
        </>
      ) : (
        <>
          <DefRow label="Car">{b.car.title ?? b.car.slug ?? '—'}</DefRow>
          <DefRow label="Slug">
            <span className="font-mono text-[12px]">{b.car.slug ?? '—'}</span>
          </DefRow>
          <DefRow label="Location">{b.location ? b.location.name ?? b.location.slug : '—'}</DefRow>
        </>
      )}
      <DefRow label={isTransferBooking(b) ? 'Crew' : 'Vendor'}>
        {b.vendor.businessName ?? b.vendor.email ?? '—'}
        <AssignmentBadge assignedBy={b.assignedBy} />
      </DefRow>
      {/* THE OTHER LEG. A round trip is two bookings so a different crew can
          serve each, but one payment covers both and they cancel together —
          ops fielding a call about one needs the other in view, not a search
          away. Each leg keeps its own row, because each has its own crew,
          driver and status. */}
      {b.otherLeg ? (
        <DefRow label={b.otherLeg.tripLeg === 'return' ? 'Return leg' : 'Outbound leg'}>
          <button
            type="button"
            onClick={() =>
              void navigate({
                to: '/bookings/$reference' as never,
                params: { reference: b.otherLeg!.reference } as never,
              })
            }
            className="font-mono text-[12px] text-[color:var(--bo-accent)] hover:underline"
          >
            {b.otherLeg.reference}
          </button>
          <span className="ms-2 text-[11.5px] text-[color:var(--bo-mute)]">
            {fmtDay(b.otherLeg.dateFrom)}
            {b.otherLeg.pickupHour != null ? fmtHour(b.otherLeg.pickupHour) : ''} ·{' '}
            {b.otherLeg.status}
          </span>
        </DefRow>
      ) : null}
      <button
        type="button"
        onClick={() =>
          void navigate({
            to: '/crew/$id' as never,
            params: { id: String(b.vendor.id) } as never,
          })
        }
        className="mt-3 text-[12px] font-medium text-[color:var(--bo-accent)] hover:underline"
      >
        View vendor →
      </button>
    </DetailSection>
  );

  // Split out of the old single "Financials" block. Fifteen rows in one column
  // was the main reason this page read as a wall: what the booking is worth and
  // how it was paid are different questions, asked by different people.
  const chargesSection = (
    <DetailSection title="Charges">
      <DefRow label="Subtotal">{fmtMoney(b.subtotal, b.currency)}</DefRow>
      <DefRow label="Discount">{fmtMoney(b.discount, b.currency)}</DefRow>
      <DefRow label="Wallet applied">{fmtMoney(b.walletApplied, b.currency)}</DefRow>
      <DefRow label="Tax">{fmtMoney(b.tax, b.currency)}</DefRow>
      <DefRow label="Total (customer paid)">
        <strong>
          {bookedCur
            ? fmtMoney(b.purchaseTotal ?? toBooked(b.total), bookedCur)
            : fmtMoney(b.total, b.currency)}
        </strong>
        {bookedCur ? (
          <span className="ms-1 text-[11px] text-[color:var(--bo-mute)]">
            · {fmtMoney(b.total, b.currency)} ledger
          </span>
        ) : null}
      </DefRow>
      <DefRow label="Commission">{fmtMoney(b.commissionAmount, b.currency)}</DefRow>
      <DefRow label="Authorized (held)">
        {b.authorizedAmount != null ? fmtMoney(b.authorizedAmount, b.currency) : '—'}
      </DefRow>
      {b.fee != null ? (
        <DefRow label="Platform fee (card)">
          {fmtMoney(b.fee, b.currency)}
          {bookedDiffers ? (
            <span className="text-[color:var(--bo-mute)]"> ·{fmtBooked(b.fee)}</span>
          ) : null}
        </DefRow>
      ) : null}
      {b.cardFee != null ? (
        <DefRow label="Card processing fee">{fmtMoney(b.cardFee, b.currency)}</DefRow>
      ) : null}
      {b.bidPrice != null ? (
        <DefRow label={`Cash to crew${vendorCur ? ` (${vendorCur})` : ''}`}>
          <strong>{fmtVendor(b.bidPrice)}</strong>
          <span className="ms-1 text-[11px] text-[color:var(--bo-mute)]">
            {bookedDiffers ? `· ${fmtBooked(b.bidPrice)} ` : ''}· {fmtMoney(b.bidPrice, b.currency)}{' '}
            ledger
          </span>
        </DefRow>
      ) : null}
      {b.capturedAmount != null ? (
        <DefRow label="Card captured">
          {fmtMoney(b.capturedAmount, b.owedCurrency ?? b.currency)}
        </DefRow>
      ) : null}
      {b.paidTopUps != null && b.paidTopUps >= 0.01 ? (
        <DefRow label="Top-up collected">
          <span className="font-semibold text-[#15803d]">
            {fmtMoney(b.paidTopUps, b.owedCurrency ?? b.currency)}
          </span>
          <span className="ms-1 text-[11px] text-[color:var(--bo-mute)]">
            — via Request payment
          </span>
        </DefRow>
      ) : null}
      {b.amountOwed != null ? (
        <DefRow label="Amount owed">
          {b.amountOwed >= 0.01 ? (
            <>
              <span className="font-semibold text-[#9f1239]">
                {fmtMoney(b.amountOwed, b.owedCurrency ?? b.currency)}
              </span>
              <span className="ms-1 text-[11px] text-[color:var(--bo-mute)]">
                — collect via Request payment
              </span>
            </>
          ) : (
            <span className="font-semibold text-[#15803d]">Collected ✓</span>
          )}
        </DefRow>
      ) : null}
    </DetailSection>
  );

  const paymentSection = (
    <DetailSection title="Payment">
      <DefRow label="Payment">
        {b.paymentStatus ?? 'unpaid'} · {b.paymentProvider ?? '—'}
      </DefRow>
      <DefRow label="Method">
        <MethodChip method={b.paymentMethod} />
      </DefRow>
      {b.paymentProcessingFeePct > 0 ? (
        <DefRow label="Processing fee">{Number(b.paymentProcessingFeePct)}%</DefRow>
      ) : null}
      {b.paymentLockedCurrency ? (
        <DefRow label="Charge currency">{b.paymentLockedCurrency}</DefRow>
      ) : null}
      {b.paymentLockedAmountMinor != null && b.paymentLockedCurrency ? (
        <DefRow label="Locked amount (Stripe charge)">
          {fmtLockedAmount(b.paymentLockedAmountMinor, b.paymentLockedCurrency)}
        </DefRow>
      ) : null}
      {/* Host payout — for card the platform settles via Stripe Connect
          automatically, so the action is N/A. For klarna/crypto finance
          has to mark the cycle done by hand (SEPA, cash, etc.). */}
      <DefRow label="Host payout">
        {b.hostPayoutCompletedAt ? (
          <span>Completed {fmtDate(b.hostPayoutCompletedAt)}</span>
        ) : b.paymentMethod === 'klarna' || b.paymentMethod === 'crypto' ? (
          <div className="flex flex-col gap-1">
            <RowAction
              kind="primary"
              busy={markHostPayoutMut.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    'Mark host payout as paid? This records the date and cannot be undone.',
                  )
                )
                  return;
                markHostPayoutMut.mutate(b.reference);
              }}
            >
              Mark host payout paid
            </RowAction>
            {markHostPayoutMut.error ? (
              <span className="text-[11.5px] text-[#9f1239]">
                {(() => {
                  const err = markHostPayoutMut.error;
                  const body =
                    err instanceof ApiError ? (err.body as { error?: string } | null) : null;
                  return body?.error ?? (err as Error).message;
                })()}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="text-[color:var(--bo-mute)]">N/A (card — auto-settled)</span>
        )}
      </DefRow>
      {/* Documentation-requirements acknowledgement — set at cart-step
          when the customer ticked the platform's collection-docs checkbox.
          Null on legacy bookings + (intentionally) on rows where the
          policy was disabled at the time. Surfaced here so dispute /
          chargeback investigations can prove the customer agreed. */}
      <DefRow label="Documents acknowledged">
        {b.documentsAcknowledgedAt ? (
          <span>{fmtDate(b.documentsAcknowledgedAt)}</span>
        ) : (
          <span className="text-[color:var(--bo-mute)]">Not acknowledged</span>
        )}
      </DefRow>
    </DetailSection>
  );

  // Bid reconciliation — INTERNAL/FINANCE ONLY. Surfaced for klarna/crypto
  // where the customer prepays one fiat currency and the host gets paid
  // out-of-band in a possibly-different currency. Customers must never
  // see this block.
  const bidReconciliationSection = (
    <DetailSection title="Bid reconciliation (finance only)">
      <DefRow label="Bid amount (host)">
        {b.bidAmountBuy != null && b.buyCurrency
          ? `${b.bidAmountBuy.toFixed(2)} ${b.buyCurrency}`
          : '—'}
      </DefRow>
      <DefRow label="Customer prepaid">
        {fmtLockedAmount(b.paymentLockedAmountMinor, b.paymentLockedCurrency)}
      </DefRow>
      {(() => {
        // Only compute platform margin when the two figures share a
        // currency; otherwise raw subtraction silently mixes ledgers (e.g.
        // EUR prepay vs MAD bid). See [feedback_widget_pricing_math].
        const sameCcy =
          b.buyCurrency && b.paymentLockedCurrency && b.buyCurrency === b.paymentLockedCurrency;
        if (!sameCcy || b.bidAmountBuy == null || b.paymentLockedAmountMinor == null) {
          return (
            <DefRow label="Platform margin">
              <span className="text-[color:var(--bo-mute)]">
                currencies differ — see finance dashboard for conversion
              </span>
            </DefRow>
          );
        }
        const prepaid = b.paymentLockedAmountMinor / 100;
        const delta = prepaid - b.bidAmountBuy;
        const label = delta >= 0 ? 'Platform margin' : 'Platform shortfall';
        const color = delta >= 0 ? 'text-emerald-700' : 'text-red-700';
        return (
          <DefRow label={label}>
            <span className={color}>
              {Math.abs(delta).toFixed(2)} {b.paymentLockedCurrency}
            </span>
          </DefRow>
        );
      })()}
      <p className="mt-2 text-[11.5px] italic text-[color:var(--bo-mute)]">
        Customer is not shown this reconciliation. It exists only for finance.
      </p>
    </DetailSection>
  );

  const timelineSection = (
    <DetailSection title="Timeline">
      {b.timeline.length === 0 ? (
        <div className="text-[12.5px] text-[color:var(--bo-mute)]">No status events recorded.</div>
      ) : (
        <ol className="relative ms-3 space-y-3 border-s border-[color:var(--bo-line)] ps-5">
          {b.timeline.map((t) => (
            <li key={t.event}>
              {/* start-0 anchors the dot to the rail. Without it the absolute
                  box takes its static position — after the ol's 20px
                  padding-inline-start — so the dots sat 20px right of the line
                  they are meant to sit on, and the -5.5px pull-back could not
                  close that gap. -ms-[4.5px] then centres the 8px dot on the
                  1px border. */}
              <span className="absolute start-0 -ms-[4.5px] mt-1.5 inline-block h-2 w-2 rounded-full bg-[color:var(--bo-accent)]" />
              <div className="text-[12.5px] font-semibold capitalize">{t.event}</div>
              <div className="text-[11.5px] text-[color:var(--bo-mute)]">{fmtDate(t.at)}</div>
            </li>
          ))}
        </ol>
      )}
    </DetailSection>
  );

  // Work waiting on an operator. Drives the dot on the Actions tab so the tab
  // strip answers "is there anything to do here?" without a click — otherwise
  // tabs HIDE the pending work that the old stacked layout at least showed.
  // `ready` is open work too — it is the state a booking is parked in FOR an
  // operator. Treating only `pending` as open hid the match tools at exactly
  // the moment they were needed (see booking-bid-state.ts).
  const bidWork = bidWorkFor(b);
  const bidOpen = bidWork.open;
  const canRequestPayment = b.status === 'confirmed' || b.status === 'completed';
  const owes = (b.amountOwed ?? 0) >= 0.01;
  const needsAction = bidOpen || owes;

  const tabs: DetailTab[] = [
    {
      id: 'overview',
      label: 'Overview',
      // CSS columns, not a grid. These four cards have unrelated, unequal
      // heights, and a grid gives you a choice of two bad rows: stretch every
      // card to the tallest in its row (dead whitespace inside the short ones),
      // or align to the top and get ragged bottoms plus an orphaned fourth card
      // marooned below a gap. Column flow packs them by height instead, so the
      // columns end near-level and nothing is stranded.
      //
      // break-inside-avoid stops a card being split across a column boundary.
      // Order matters: CSS columns fill by source order, not by height, so the
      // consistently-tallest card (Car & vendor) leads to keep the columns
      // level. Fulfilment is last because it is absent entirely on bookings
      // with no fulfilment data.
      content: (
        <div className="columns-1 gap-4 lg:columns-3 [&>*]:mb-4 [&>*]:break-inside-avoid">
          {carVendorSection}
          {customerSection}
          {bookingSection}
          {fulfilmentSection}
        </div>
      ),
    },
    {
      id: 'money',
      label: 'Money',
      content: (
        // Columns for the same reason as Overview: Charges runs ~13 rows and
        // Payment ~6, so a grid either stretches Payment into dead space or
        // leaves a ragged bottom.
        <div className="columns-1 gap-4 lg:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">
          {/* The reconciled answer leads; Charges below is the raw ledger. */}
          <MoneySection money={b.money} />
          {chargesSection}
          {paymentSection}
          {/* Finance-only, and only for the prepay methods where the customer
              and the host settle in possibly-different currencies. */}
          {b.paymentMethod === 'klarna' || b.paymentMethod === 'crypto'
            ? bidReconciliationSection
            : null}
        </div>
      ),
    },
    // Only when there is something to act on. A permanently-present "Actions"
    // tab that is usually empty trains operators to ignore it.
    ...(bidOpen || canRequestPayment
      ? [
          {
            id: 'actions',
            label: 'Actions',
            badge: needsAction ? (
              <span
                aria-label="needs attention"
                className="inline-block h-1.5 w-1.5 rounded-full bg-[#e11d48]"
              />
            ) : undefined,
            content: (
              <div className="space-y-4">
                {bidOpen ? (
                  <>
                    <BidReadyNotice work={bidWork} />
                    <BidsPanel booking={b} onApproved={invalidate} />
                    <MatchCard booking={b} onMatched={invalidate} />
                  </>
                ) : null}
                {canRequestPayment ? <PaymentRequestCard booking={b} /> : null}
              </div>
            ),
          },
        ]
      : []),
    {
      id: 'history',
      label: 'History',
      content: (
        <div className="space-y-4">
          {timelineSection}
          {b.cancellationReason ? (
            <DetailSection title="Cancellation reason">
              <p className="text-[13px] text-[color:var(--bo-ink)]">{b.cancellationReason}</p>
            </DetailSection>
          ) : null}
          <EntityActivity entityType="bookings" entityId={b.id} />
        </div>
      ),
    },
  ];

  return (
    <DetailLayout
      eyebrow={`Operate · ${bookingTypeLabel(b)}`}
      title={b.reference}
      sub={
        isTransferBooking(b)
          ? // A transfer is ONE day at an hour, not a range. "27 Sep → 27 Sep"
            // reads as a data error; the pick-up time is the operative fact.
            `${fmtDate(b.dateFrom)}${b.pickupHour != null ? ` · ${String(b.pickupHour).padStart(2, '0')}:00` : ''} · ${fmtMoney(b.total, b.currency)}`
          : `${fmtDate(b.dateFrom)} → ${fmtDate(b.dateTo)} · ${fmtMoney(b.total, b.currency)}`
      }
      backTo="/bookings"
      backLabel="All bookings"
      actions={
        <>
          {b.status === 'pending' ? (
            <>
              {/* Confirm is hidden while the bid is still open — confirming an
                  un-matched booking strands it (no crew/capture/bid). Set the
                  bid below (which confirms itself) instead. Same while it is
                  parked at `ready` for an operator to match. Server enforces
                  this too (409 bid_not_set). */}
              {canConfirmWithoutMatch(b) ? (
                <RowAction kind="primary" busy={busy} onClick={() => confirmMut.mutate(b.reference)}>
                  Confirm
                </RowAction>
              ) : null}
              <RowAction
                kind="danger"
                busy={busy}
                onClick={() => {
                  const reason = window.prompt('Cancellation reason (optional):') ?? undefined;
                  cancelMut.mutate({ ref: b.reference, reason });
                }}
              >
                Cancel
              </RowAction>
            </>
          ) : null}
          {b.status === 'confirmed' ? (
            <>
              <RowAction busy={busy} onClick={() => completeMut.mutate(b.reference)}>
                Mark complete
              </RowAction>
              <RowAction
                kind="danger"
                busy={busy}
                onClick={() => {
                  const reason = window.prompt('Cancellation reason (optional):') ?? undefined;
                  cancelMut.mutate({ ref: b.reference, reason });
                }}
              >
                Cancel
              </RowAction>
            </>
          ) : null}
          <StatusPill status={b.status} />
        </>
      }
    >
      <DetailTabs tabs={tabs} />
    </DetailLayout>
  );
}

// ─── Match card ──────────────────────────────────────────────────────
//
// Admin-mediated matching: set a negotiated base price (per unit/day, in the
// booking currency) + assign a host, then POST /admin/bookings/:ref/match.
// The backend either confirms the booking (negotiated <= system base →
// capture + confirm) or, when the price is higher, mints a repayment link
// and emails it to the customer.

const inputCls =
  'mt-1 h-10 w-full rounded-lg border border-[color:var(--bo-line)] px-3 text-[13.5px] outline-none focus:border-[color:var(--bo-accent)] disabled:bg-[color:var(--bo-bg)]';
const labelCls =
  'text-[11.5px] font-semibold uppercase tracking-wide text-[color:var(--bo-mute)]';

export function BidsPanel({
  booking,
  onApproved,
}: {
  booking: AdminBookingDetail;
  onApproved: () => void;
}) {
  const bidsQuery = useQuery({
    queryKey: ['admin', 'booking-bids', booking.reference],
    queryFn: ({ signal }) => fetchBookingBids(booking.reference, signal),
  });
  const approveMut = useMutation({
    mutationFn: (vars: { hostId: number; basePrice: number; currency: string }) =>
      matchBooking(booking.reference, vars),
    onSuccess: onApproved,
  });

  const bids = bidsQuery.data?.bids ?? [];
  if (bids.length === 0) return null; // nothing to show until fleets bid

  // A fixed-price sale: the advertised base is a ceiling, so a bid above it
  // cannot be used — it would ask the customer for more than they were sold.
  const work = bidWorkFor(booking);
  const advertisedPerDay = booking.systemBasePrice ?? null;

  const cheapestId = bids[0]?.id;
  const currency = booking.currency ?? 'EUR';

  return (
    <DetailSection title={`Bids received (${bids.length})`}>
      <p className="mb-3 text-[12.5px] text-[color:var(--bo-mute)]">
        Cheapest first. &quot;Use this bid&quot; assigns the crew and sets the negotiated base price.
      </p>
      <div className="space-y-2">
        {bids.map((bid) => {
          const busy = approveMut.isPending && approveMut.variables?.hostId === bid.hostId;
          const usable = bidIsUsable(work, bid.amount, advertisedPerDay);
          return (
            <div
              key={bid.id}
              className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                bid.id === cheapestId ? 'border-[color:var(--bo-accent)]' : 'border-[color:var(--bo-line)]'
              }`}
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium">
                  {bid.crewName ?? bid.crewEmail ?? `Crew #${bid.hostId}`}
                  {bid.id === cheapestId ? (
                    <span className="ms-2 rounded-full bg-[color:var(--bo-accent)]/10 px-2 py-0.5 text-[10.5px] font-semibold text-[color:var(--bo-accent)]">
                      Cheapest
                    </span>
                  ) : null}
                  {bid.autoAssigned ? (
                    <AssignmentBadge assignedBy="auto_assign" />
                  ) : null}
                </div>
                <div className="text-[11.5px] text-[color:var(--bo-mute)]">{fmtDate(bid.submittedAt)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="num-tab text-[14px] font-semibold">{fmtMoney(bid.amount, currency)}/day</span>
                <button
                  type="button"
                  disabled={busy || !usable}
                  title={usable ? undefined : 'Above the advertised price — a fixed-price customer is never asked for more.'}
                  onClick={() => approveMut.mutate(matchInputForBid(bid, currency))}
                  className="rounded-full bg-[color:var(--bo-ink)] px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-50"
                >
                  {busy ? 'Approving…' : usable ? 'Use this bid' : 'Above advertised'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {approveMut.error ? (
        <p className="mt-2 text-[12.5px] text-[#9f1239]">Approve failed: {matchErrorText(approveMut.error)}</p>
      ) : null}
    </DetailSection>
  );
}

/** The API's match refusals, in an operator's words. */
export function matchErrorText(err: unknown): string {
  const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
  const code = body?.error ?? (err as Error | null)?.message ?? '';
  if (code.includes('fixed_price_above_advertised')) {
    const max = (body as { maxBasePerDay?: number; currency?: string } | null);
    const limit = max?.maxBasePerDay != null ? ` The most it can be is ${max.currency ?? ''} ${max.maxBasePerDay.toFixed(2)}/day.` : '';
    return `This is a fixed-price booking: the crew base cannot be above the advertised price.${limit}`;
  }
  return code || 'Match failed.';
}

/** Tells the operator WHICH job a parked (`ready`) booking is. Renders nothing
 *  while the bid is merely pending — there is no instruction to give yet. */
/**
 * THE MONEY. The Charges block above is the ledger as stored — three
 * currencies, several of them EUR figures the customer never saw. This block
 * is the one reconciled answer, every line in the currency the customer was
 * actually charged (API: modules/bookings/money.ts).
 *
 * Exported so the numbers can be rendered and read in a test rather than
 * inferred from the source.
 */
/** How a sale earned, in words (0241). The column values are database enums —
 *  `supplier_commission` is not a phrase anyone says out loud — so the screen
 *  names the model rather than printing it. An unknown value (a model added
 *  after this build) prints nothing rather than a raw enum. */
const MODEL_LABEL: Record<string, string> = {
  booking_fee: 'Booking fee',
  supplier_commission: 'Supplier commission',
  markup_supplier_commission: 'Markup + supplier commission',
};

export function MoneySection({ money }: { money: BookingMoney | null | undefined }) {
  if (!money) return null;
  const cur = money.currency;
  const modelLabel = money.model ? MODEL_LABEL[money.model] : undefined;
  // The two lanes only two of the three models have. Shown when there is
  // something IN them — a "Markup €0.00" row on every booking-fee sale would
  // be noise on the page an operator opens most, and the model row above
  // already says why they are absent.
  const markup = money.markup ?? 0;
  const supplierCommission = money.supplierCommission ?? 0;
  return (
    <DetailSection title="Money">
      {modelLabel ? <DefRow label="Model">{modelLabel}</DefRow> : null}
      <DefRow label="Customer paid">{fmtMoney(money.customerPaid, cur)}</DefRow>
      <DefRow label="Crew share">{fmtMoney(money.crewShare, cur)}</DefRow>
      {markup !== 0 ? (
        <DefRow label="Markup">{fmtMoney(markup, cur)}</DefRow>
      ) : null}
      {supplierCommission !== 0 ? (
        <DefRow label="Supplier commission">
          {fmtMoney(supplierCommission, cur)}
          <span className="ms-1 text-[11px] text-[color:var(--bo-mute)]">
            — owed by the crew on the base it collected, billed on its statement
          </span>
        </DefRow>
      ) : null}
      <DefRow label="Partner commission">
        {fmtMoney(money.partnerCommission, cur)}
        {money.commissionUnconverted ? (
          <span className="ms-1 text-[11px] text-[color:var(--bo-mute)]">
            — not converted into {cur}, so it is not in the net below
          </span>
        ) : null}
      </DefRow>
      <DefRow label="Card fee">
        {money.cardFee != null ? fmtMoney(money.cardFee, cur) : '—'}
      </DefRow>
      <DefRow label="DriveCars net">
        <strong>{fmtMoney(money.drivecarsNet, cur)}</strong>
      </DefRow>
      {!money.settled ? (
        <div role="note" className="pt-1 text-[11.5px] text-[color:var(--bo-mute)]">
          Estimate until the crew is assigned and the card is settled.
        </div>
      ) : null}
    </DetailSection>
  );
}

export function BidReadyNotice({ work }: { work: BidWork }) {
  if (work.parkedReason == null) return null;
  const fixed = work.parkedReason === 'match_advertised';
  return (
    <div
      role="status"
      className={`rounded-lg border px-4 py-3 text-[13px] ${
        fixed
          ? 'border-[#f59e0b]/40 bg-[#fffbeb] text-[#78350f]'
          : 'border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] text-[color:var(--bo-ink)]'
      }`}
    >
      <div className="font-semibold">
        {fixed ? 'Match at the advertised price' : 'Pick the winning bid'}
      </div>
      <p className="mt-1">
        {fixed
          ? 'Fixed-price partner booking. It could not assign itself when the bid window closed. Match a crew at the advertised base price below — never above it. The customer was sold a firm price and will not be asked for more, so bids above it are disabled.'
          : 'The bid window closed with bids on file. Choose the winning bid below, or set a base price and crew by hand.'}
      </p>
    </div>
  );
}

function MatchCard({
  booking,
  onMatched,
}: {
  booking: AdminBookingDetail;
  onMatched: () => void;
}) {
  // Default the negotiated base to the raw system base price (the host base
  // snapshot the backend compares against), not the marked-up pricePerUnit, so
  // the common "accept the system base" case is one click and doesn't trip the
  // higher-price repayment path. Fall back to pricePerUnit only if absent.
  const systemBase = booking.systemBasePrice ?? booking.pricePerUnit;
  // Currency the admin enters the crew's base in. Default to the VENDOR's base
  // currency (the car's pricing currency, e.g. MAD) — that's what the crew is
  // settled in and what the ledger's sell side must be. Defaulting to the
  // customer's booked currency mislabelled the sell side (the DC-2026-B7N77Q
  // bug). Admin can still switch it. The ledger is EUR; we convert on submit.
  const currencies =
    booking.displayCurrencies && booking.displayCurrencies.length > 0
      ? booking.displayCurrencies
      : [{ code: booking.currency, symbol: '', rateToPrimary: 1 }];
  const bookedCur = booking.purchaseCurrency || booking.currency;
  const vendorCur = booking.vendorCurrency || bookedCur;
  const rateOf = (code: string) =>
    currencies.find((x) => x.code === code)?.rateToPrimary ?? 1;
  // primary(EUR) → display currency
  const toCur = (eur: number, code: string) => {
    const r = rateOf(code);
    return r > 0 ? eur / r : eur;
  };
  const [bidCurrency, setBidCurrency] = useState<string>(vendorCur);
  // A fixed-price sale prefills the server's own ceiling (floored, in the
  // crew's currency) rather than a locally rounded conversion of the EUR base:
  // rounding half a cent UP is enough to land a match one cent over a price
  // the customer was promised would not move.
  const fixedCeiling =
    booking.fixedCeiling && booking.fixedCeiling.currency === vendorCur ? booking.fixedCeiling.perDay : null;
  const [basePrice, setBasePrice] = useState<string>(
    fixedCeiling != null
      ? String(fixedCeiling)
      : systemBase != null
        ? String(Math.round(toCur(systemBase, vendorCur) * 100) / 100)
        : '',
  );
  const [hostId, setHostId] = useState<string>(String(booking.vendor?.id ?? ''));
  const [hostEmail, setHostEmail] = useState<string>(booking.vendor?.email ?? '');
  const [result, setResult] = useState<MatchBookingResult | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const hostsQuery = useQuery({
    queryKey: ['admin', 'hosts'],
    queryFn: ({ signal }) => listHosts(signal),
  });

  const matchMut = useMutation({
    mutationFn: () =>
      matchBooking(booking.reference, {
        basePrice: Number(basePrice),
        hostId: Number(hostId),
        currency: bidCurrency,
      }),
    onSuccess: (res) => {
      setResult(res);
      setErrMsg(null);
      onMatched();
    },
    onError: (err) => {
      setResult(null);
      setErrMsg(matchErrorText(err));
    },
  });

  const valid = Number(basePrice) > 0 && Number(hostId) > 0;
  const busy = matchMut.isPending;

  return (
    <DetailSection title="Confirm booking — set negotiated price + assign host">
      <p className="mb-4 text-[12.5px] text-[color:var(--bo-mute)]">
        Enter the negotiated base price you agreed with the customer (per day, in the currency you
        choose) and assign the host. If the negotiated price is at or below the system rate the
        booking is confirmed and both parties are notified; if it is higher a payment link is sent
        to the customer to cover the difference.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className={labelCls}>Currency</span>
          <select
            value={bidCurrency}
            disabled={busy}
            onChange={(e) => {
              const next = e.target.value;
              setBidCurrency(next);
              // Reset the base to the system rate expressed in the new currency.
              if (systemBase != null) {
                setBasePrice(String(Math.round(toCur(systemBase, next) * 100) / 100));
              }
            }}
            className={inputCls}
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
                {c.code === bookedCur ? ' (booked)' : ''}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-[11px] text-[color:var(--bo-mute)]">
            Booked in {bookedCur}. Stored in {booking.currency} at the configured rate.
          </span>
        </label>

        <label className="block">
          <span className={labelCls}>Negotiated base price ({bidCurrency} / day)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={basePrice}
            disabled={busy}
            onChange={(e) => setBasePrice(e.target.value)}
            className={inputCls}
          />
          <span className="mt-1 block text-[11px] text-[color:var(--bo-mute)]">
            System base price / day: {fmtMoney(toCur(systemBase, bidCurrency), bidCurrency)} ·{' '}
            {booking.units} unit(s)
          </span>
          <span className="mt-1 block text-[11px] text-[color:var(--bo-mute)]">
            Enter the per-day base in {bidCurrency}. ≤ the system rate captures the held payment;
            higher requests a new payment from the customer.
          </span>
        </label>

        <label className="block">
          <span className={labelCls}>Assign host</span>
          {hostsQuery.data && hostsQuery.data.length > 0 ? (
            <select
              value={hostId}
              disabled={busy}
              onChange={(e) => {
                setHostId(e.target.value);
                const h = hostsQuery.data?.find((x) => String(x.id) === e.target.value);
                if (h?.email) setHostEmail(h.email);
              }}
              className={inputCls}
            >
              <option value="">Select a host…</option>
              {hostsQuery.data.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="number"
              min="1"
              step="1"
              value={hostId}
              disabled={busy}
              onChange={(e) => setHostId(e.target.value)}
              placeholder="Host ID"
              className={inputCls}
            />
          )}
          {hostsQuery.isError ? (
            <span className="mt-1 block text-[11px] text-[#9f1239]">
              Could not load hosts — enter the host ID manually.
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className={labelCls}>Host email (optional)</span>
          <input
            type="email"
            value={hostEmail}
            disabled={busy}
            onChange={(e) => setHostEmail(e.target.value)}
            className={inputCls}
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <RowAction
          kind="primary"
          busy={busy}
          disabled={!valid}
          onClick={() => matchMut.mutate()}
        >
          Confirm booking
        </RowAction>
      </div>

      {result?.kind === 'confirmed' ? (
        <div className="mt-4 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] p-3 text-[12.5px] text-[#166534]">
          Booking confirmed — customer &amp; host notified.
        </div>
      ) : null}

      {result?.kind === 'repayment_required' ? (
        <div className="mt-4 rounded-lg border border-[#fde68a] bg-[#fffbeb] p-3 text-[12.5px] text-[#92400e]">
          <div className="font-semibold">
            Higher price — payment link sent to the customer.
          </div>
          <a
            href={result.payLink}
            target="_blank"
            rel="noreferrer"
            className="mt-1 block break-all font-mono text-[12px] text-[color:var(--bo-accent)] hover:underline"
          >
            {result.payLink}
          </a>
        </div>
      ) : null}

      {errMsg ? (
        <div className="mt-4 rounded-lg border border-[#fecaca] bg-[#fff1f2] p-3 text-[12.5px] text-[#9f1239]">
          {errMsg}
        </div>
      ) : null}
    </DetailSection>
  );
}

// ─── Payment request card ────────────────────────────────────────────
//
// Mint a booking-tied payment link to collect an extra charge on a confirmed
// booking (price-fix top-up, deposit, damage). The customer pays it by card at
// the /pay link; the charge is recorded against the booking in app.payment_links.
// Charge in the booking's buy/purchase currency for a clean reconcile.

const REASON_LABELS: Record<PaymentRequestReason, string> = {
  top_up: 'Top-up / price adjustment',
  deposit: 'Deposit',
  damage_charge: 'Damage charge',
};

function PaymentRequestCard({ booking }: { booking: AdminBookingDetail }) {
  const currencies =
    booking.displayCurrencies && booking.displayCurrencies.length > 0
      ? booking.displayCurrencies
      : [{ code: booking.currency, symbol: '', rateToPrimary: 1 }];
  const defaultCur = booking.buyCurrency || booking.purchaseCurrency || booking.currency;

  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>(defaultCur);
  const [reason, setReason] = useState<PaymentRequestReason>('top_up');
  const [result, setResult] = useState<PaymentRequestResult | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      requestBookingPayment(booking.reference, { amount: Number(amount), currency, reason }),
    onSuccess: (res) => {
      setResult(res);
      setErrMsg(null);
      setCopied(false);
    },
    onError: (err) => {
      setResult(null);
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setErrMsg(body?.error ?? (err as Error).message ?? 'Could not create the payment request.');
    },
  });

  const valid = Number(amount) > 0 && currency.trim().length === 3;
  const busy = mut.isPending;

  const copyLink = () => {
    if (!result) return;
    void navigator.clipboard?.writeText(result.url).then(() => setCopied(true));
  };

  return (
    <DetailSection title="Request payment — collect an extra charge">
      <p className="mb-4 text-[12.5px] text-[color:var(--bo-mute)]">
        Mint a payment link the customer pays by card; the charge is recorded against this booking.
        Enter the amount in the currency you want to charge — use the booked currency ({defaultCur})
        for a clean reconcile.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className={labelCls}>Reason</span>
          <select
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value as PaymentRequestReason)}
            className={inputCls}
          >
            {(Object.keys(REASON_LABELS) as PaymentRequestReason[]).map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r]}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className={labelCls}>Currency</span>
          <select
            value={currency}
            disabled={busy}
            onChange={(e) => setCurrency(e.target.value)}
            className={inputCls}
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
                {c.code === defaultCur ? ' (booked)' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className={labelCls}>Amount ({currency})</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            disabled={busy}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className={inputCls}
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <RowAction kind="primary" busy={busy} disabled={!valid} onClick={() => mut.mutate()}>
          Create payment request
        </RowAction>
      </div>

      {result ? (
        <div className="mt-4 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] p-3 text-[12.5px] text-[#166534]">
          <div className="font-semibold">Payment request created — send this link to the customer.</div>
          <a
            href={result.url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 block break-all font-mono text-[12px] text-[color:var(--bo-accent)] hover:underline"
          >
            {result.url}
          </a>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={copyLink}
              className="rounded-full bg-[color:var(--bo-ink)] px-3 py-1.5 text-[12px] font-medium text-white"
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <span className="text-[11px] text-[color:var(--bo-mute)]">
              Expires {fmtDate(result.expiresAt)}
            </span>
          </div>
        </div>
      ) : null}

      {errMsg ? (
        <div className="mt-4 rounded-lg border border-[#fecaca] bg-[#fff1f2] p-3 text-[12.5px] text-[#9f1239]">
          {errMsg}
        </div>
      ) : null}
    </DetailSection>
  );
}
